import React, { FunctionComponent, useMemo, useReducer } from 'react';
import styles from './case-study-list.module.scss';
import {
  ICaseStudyItem,
  CaseStudyListItem
} from '../case-study-list/case-study-list-item/case-study-article-list-item';
import { Container } from '../container/container.component';
import { ButtonLoader } from '../button-loader/button-loader';
import {
  CaseStudyListAction,
  CaseStudyListActionType,
  CaseStudyListState,
  ICaseStudyListProps
} from './case-study-list-types';
import { CaseStudyCategoryFilter } from './case-study-category/case-study-category';
import { getAllCategories } from './getAllCategories';

const articlesToShow = 6;

const initialState: CaseStudyListState = {
  articlesListLength: articlesToShow,
  filters: []
};

const CaseStudyListComponent: FunctionComponent<ICaseStudyListProps> = ({
  buttonText,
  articles,
  caseStudyCategories,
  industryCategoryText,
  projectCategoryText,
  technologyCategoryText,
  countryCategoryText
}) => {
  const [{ articlesListLength, filters }, caseStudyListDispatch] = useReducer(
    reducer,
    initialState
  );

  const filteredData = useMemo(
    () =>
      articles.filter(
        (v: ICaseStudyItem, i: number, a: ICaseStudyItem[]) =>
          a.findIndex(t => t.id === v.id && t.language === v.language) === i
      ),
    [articles]
  );

  const filterByCateogryHandler = useMemo(() => {
    if (!filters.length) {
      return filteredData;
    }
    const filtered = filteredData.filter((caseStudy): boolean => {
      const allCategories = getAllCategories(caseStudy);
      if (!allCategories) {
      }

      return allCategories.some(category =>
        filters.some(filter => filter === category)
      );
    });
    return filtered;
  }, [filteredData, filters]);

  const articlesList = filterByCateogryHandler.slice(0, articlesListLength);

  const onButtonClick = (): void => {
    caseStudyListDispatch({
      type: CaseStudyListActionType.SET_ARTICLES_COUNT,
      payload: { articlesCount: articlesListLength + articlesToShow }
    });
  };

  const setFilterHandler = (
    event: React.ChangeEvent<HTMLInputElement>,
    filter: string
  ): void => {
    event.target.checked
      ? caseStudyListDispatch({
          type: CaseStudyListActionType.SET_FILTER,
          payload: { filter }
        })
      : caseStudyListDispatch({
          type: CaseStudyListActionType.REMOVE_FILTER,
          payload: { filter }
        });
  };

  const clearAllHandler = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    filters: string[]
  ): void => {
    event.preventDefault();

    if (!filters.length) {
      return;
    }

    caseStudyListDispatch({
      type: CaseStudyListActionType.CLEAR_ALL,
      payload: { filters }
    });
  };

  return (
    <section className={styles.container}>
      <Container>
        <div className={styles.wrapper}>
          <div className={styles.filters}>
            {caseStudyCategories && caseStudyCategories.industryCategories && (
              <CaseStudyCategoryFilter
                clearAll={clearAllHandler}
                onChange={setFilterHandler}
                name={industryCategoryText}
                values={caseStudyCategories.industryCategories}
              />
            )}
            {caseStudyCategories && caseStudyCategories.projectCategories && (
              <CaseStudyCategoryFilter
                clearAll={clearAllHandler}
                onChange={setFilterHandler}
                name={projectCategoryText}
                values={caseStudyCategories.projectCategories}
              />
            )}
            {caseStudyCategories &&
              caseStudyCategories.technologyCategories && (
                <CaseStudyCategoryFilter
                  clearAll={clearAllHandler}
                  onChange={setFilterHandler}
                  name={technologyCategoryText}
                  values={caseStudyCategories.technologyCategories}
                />
              )}
            {caseStudyCategories && caseStudyCategories.countryCategories && (
              <CaseStudyCategoryFilter
                clearAll={clearAllHandler}
                onChange={setFilterHandler}
                name={countryCategoryText}
                values={caseStudyCategories.countryCategories}
              />
            )}
          </div>
          <React.Fragment>
            <div className={styles.articles}>
              {articlesList.map((article: ICaseStudyItem) => (
                <CaseStudyListItem data={article} key={article.id} />
              ))}
            </div>
            {articlesListLength < filteredData.length && (
              <div className={styles.loader}>
                <ButtonLoader buttonText={buttonText} onClick={onButtonClick} />
              </div>
            )}
          </React.Fragment>
        </div>
      </Container>
    </section>
  );
};

export const CaseStudyList = React.memo(CaseStudyListComponent);

function reducer(
  state: CaseStudyListState,
  { type, payload }: CaseStudyListAction
): CaseStudyListState {
  switch (type) {
    case CaseStudyListActionType.SET_ARTICLES_COUNT:
      return {
        ...state,
        articlesListLength: payload?.articlesCount
          ? payload.articlesCount
          : state.articlesListLength
      };
    case CaseStudyListActionType.SET_FILTER:
      if (!payload?.filter) {
        return state;
      }

      return {
        ...state,
        filters: [...state.filters, payload.filter]
      };
    case CaseStudyListActionType.REMOVE_FILTER:
      if (!payload?.filter) {
        return state;
      }

      return {
        ...state,
        filters: [...state.filters.filter(filter => filter !== payload.filter)]
      };
    case CaseStudyListActionType.CLEAR_ALL:
      if (!payload?.filters?.length) {
        return state;
      }

      return {
        ...state,
        filters: [
          ...state.filters.filter(
            filter => filter && !payload.filters?.includes(filter)
          )
        ]
      };
    default:
      return state;
  }
}
